import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { WorkflowRequest, WorkflowResponse, APIResponse } from '../types/api.types';

class APIService {
  private instance: AxiosInstance;
  private userId: string = 'default_user';

  constructor() {
    const baseUrl = window.location.origin + '/editor';
    this.instance = axios.create({
      baseURL: baseUrl,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this._setupInterceptors();
  }

  private _setupInterceptors(): void {
    // 请求拦截器, 使用cookie，不需要再手动设置token
    // this.instance.interceptors.request.use(
    //   (config: AxiosRequestConfig) => {
    //     const token = localStorage.getItem('auth_token') || '4MulDUmJ1DBtUKGXDF87PhVflulURc8F';
    //     if (token && config.headers) {
    //       config.headers.Authorization = `Bearer ${token}`;
    //     }
        
    //     config.headers = {
    //       ...config.headers,
    //       'X-User-ID': this.userId,
    //       'X-Request-ID': this._generateRequestId(),
    //     };

    //     // console.log('API Request:', config.method?.toUpperCase(), config.url);
    //     return config;
    //   },
    //   (error) => {
    //     console.error('请求拦截器错误:', error);
    //     return Promise.reject(error);
    //   }
    // );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // console.log('API Response:', response.status, response.config.url);
        return response.data;
      },
      (error) => {
        console.error('响应拦截器错误:', error);
        const loginRoute = (window as any).AGIMX_CONFIG.lOGIN_ROUTE;
        if (error.response?.status === 401) {
          // localStorage.removeItem('auth_token');
          window.location.href = loginRoute;
        } else if (error.response?.status >= 500) {
          console.error('服务器错误:', error.response.data);
        }
        
        return Promise.reject(error);
      }
    );
  }

  private _generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ========= 工作流相关接口 =========
  // async runWorkflow(question: string, tableId: string = ''): Promise<WorkflowResponse> {
  //   try {
  //     const requestData: WorkflowRequest = {
  //       inputs: { table_id: tableId, question },
  //       response_mode: 'blocking',
  //       user: this.userId,
  //     };

  //     const response = await this.instance.post<WorkflowResponse>(
  //       '/v1/workflows/run', 
  //       requestData
  //     );
      
  //     return response.data;
  //   } catch (error) {
  //     console.error('工作流API调用失败:', error);
  //     throw this._handleError(error, '生成配方失败');
  //   }
  // }

  // ========= 数据表格相关接口 =========

  /**
   * 获取表格数据
   * @param collection 集合名称
   * @returns 表格原始数据
   */
  async getTableData(collection: string, filters?: Record<string, string>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.get(`/items/${collection}`, { params: filters });
      return response;
    } catch (error) {
      console.error('获取表格数据失败:', error);
      throw this._handleError(error, '获取表格数据失败');
    }
  }

  /**
   * 更新单个数据项
   * @param collection 集合名称
   * @param id 数据项ID
   * @param updateData 更新的数据，格式：{'columnField': 'newValue'}
   * @returns 更新后的数据
   */
  async updateTableItem(collection: string, id: string, updateData: Record<string, any>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.patch(`/items/${collection}/${id}`, updateData);
      return response;
    } catch (error) {
      console.error('更新表格数据失败:', error);
      throw this._handleError(error, '更新数据失败');
    }
  }

  /**
   * 获取表格列信息
   * @param collection 集合名称
   * @returns 列信息列表
   */
  async getTableColumns(collection: string): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.get(`/fields/${collection}`);
      return response;
    } catch (error) {
      console.error('获取表格列信息失败:', error);
      throw this._handleError(error, '获取列信息失败');
    }
  }

  /**
   * 批量更新数据项
   * @param collection 集合名称
   * @param updates 批量更新数据，格式：[{id: 'xxx', data: {'field': 'value'}}]
   * @returns 更新结果
   */
  async batchUpdateTableItems(collection: string, updates: Array<{id: string, data: Record<string, any>}>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.patch(`/items/${collection}`, updates);
      return response;
    } catch (error) {
      console.error('批量更新表格数据失败:', error);
      throw this._handleError(error, '批量更新数据失败');
    }
  }

  /**
   * 创建新的数据项
   * @param collection 集合名称
   * @param data 新增的数据
   * @returns 创建结果
   */
  async createTableItem(collection: string, data: Record<string, any>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.post(`/items/${collection}`, data);
      return response;
    } catch (error) {
      console.error('创建表格数据失败:', error);
      throw this._handleError(error, '创建数据失败');
    }
  }

  /**
   * 删除数据项
   * @param collection 集合名称
   * @param id 数据项ID
   * @returns 删除结果
   */
  async deleteTableItem(collection: string, id: string): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.delete(`/items/${collection}/${id}`);
      return response;
    } catch (error) {
      console.error('删除表格数据失败:', error);
      throw this._handleError(error, '删除数据失败');
    }
  }

  // ========= 配方相关接口 =========

  /**
   * 创建配方
   * @param data 配方创建请求数据
   * @returns 创建的配方数据
   */
  public async createRecipe<T>(data: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>('/recipes', data);
  }

  /**
   * 获取配方历史
   * @param userId 用户ID
   * @returns 配方历史列表
   */
  public async getRecipeHistory<T>(userId: string): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(`/items/intelligent_table?user_id=${userId}`);
  }

  /**
   * 获取配方表名
   * @param tableId 配方ID
   * @returns 配方记录
   */
  public async getTableCollectionName<T>(tableId: string): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(`/items/intelligent_table?id=${tableId}`);
  }

  /**
   * 获取配方对话历史
   * @param userId 用户ID
   * @returns 配方对话历史列表
   */
  public async getRecipeChatHistory<T>(userId: string): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(`/items/intelligent_table_conversation_history?user_id=${userId}`);
  }

  /**
   * 删除配方
   * @param recipeId 配方ID
   * @returns 删除结果
   */
  public async deleteRecipe<T>(recipeId: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(`/items/intelligent_table/${recipeId}`);
  }

  // ========= 通用方法 =========

  private _handleError(error: any, defaultMessage: string): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error(defaultMessage);
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  getUserId(): string {
    return this.userId;
  }

  // ========= 通用 HTTP 方法 =========

  public async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data);
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  public async put<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data);
  }

  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url);
  }
}

export default new APIService();